// Shared constants + pure helpers extracted from pages/ShopProfile.js so
// the panels and the LivePreview stay DRY. Any change here still keeps
// the same shape the DB (`shops.opening_hours` JSONB, `shops.reviews`
// JSONB) already writes/reads — legacy shapes are normalised at read.

export const PRESET_COLOURS = ['#1f2a1e', '#b03a2e', '#4f46ff', '#c8a35a', '#111210'];
export const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])?$/;

// Client-side cap for photos we surface on the shop-front. The hero
// endpoint (`/upload-hero`) enforces the same limit server-side.
export const MAX_UPLOAD_BYTES = 3 * 1024 * 1024;

// Loose UK postcode regex — accepts spaces or none, upper/lower case.
// Matches formats like `M1 1AA`, `SW1A 2AA`, `EC1A1BB`, `GIR 0AA`.
// Deliberately permissive; strict Royal Mail validation is a
// follow-up (see the postcode-lookup backlog item).
export const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export const initials = (s) =>
  (s || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

// ---- Reviews ----
// Reviews shape (new): [{ id, name, review, rating, hidden }]
// Legacy shape ({ quote, author }) is normalised at read time so
// migrated rows and legacy rows both work.
export const emptyReview = () => ({
  id: (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)),
  name: '',
  review: '',
  rating: 5,
  hidden: false,
});

export const normaliseReviews = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map(r => ({
    id: r.id || (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)),
    name: r.name || r.author || '',
    review: r.review || r.quote || '',
    rating: Number(r.rating ?? 5),
    hidden: !!r.hidden,
  }));
};

// ---- Shop-level opening hours (M4) ---------------------------
//
// Stored on `shops.opening_hours` as a JSONB array of 7 rows in
// Mon..Sun display order (weekday: 1,2,3,4,5,6,0). Each row is
// { weekday, open_time: "HH:MM", close_time: "HH:MM", closed: bool }.
//
// `DAY_LABELS`, `DAY_ORDER` and `openingHoursToDisplay` are shared with
// ShopHome.js and the theme LivePreview so all three surfaces render
// identical copy.
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_ORDER  = [1, 2, 3, 4, 5, 6, 0];

export const DEFAULT_OPENING_HOURS = DAY_ORDER.map((d) => ({
  weekday: d,
  open_time: '09:00',
  close_time: '18:00',
  closed: d === 0, // Sunday closed by default
}));

/** Coerce whatever is on the DB row into a well-shaped 7-row array in
    Mon..Sun order. Fills any missing weekday with the default so the
    editor never renders fewer than 7 rows. */
export const normaliseOpeningHours = (raw) => {
  const byDay = {};
  if (Array.isArray(raw)) {
    for (const r of raw) {
      const wd = Number(r?.weekday);
      if (Number.isFinite(wd) && wd >= 0 && wd <= 6) {
        byDay[wd] = {
          weekday: wd,
          open_time: String(r.open_time || '09:00').slice(0, 5),
          close_time: String(r.close_time || '18:00').slice(0, 5),
          closed: !!r.closed,
        };
      }
    }
  }
  return DAY_ORDER.map((d) => byDay[d] || DEFAULT_OPENING_HOURS.find((x) => x.weekday === d));
};

/** Convert the stored shape into the display shape the theme LivePreview
    and ShopHome expect: [{ label, closed, start, end }]. */
export const openingHoursToDisplay = (rows) =>
  (rows || []).map((r) => ({
    label: DAY_LABELS[r.weekday],
    closed: !!r.closed,
    start: r.closed ? null : r.open_time,
    end:   r.closed ? null : r.close_time,
  }));
